import {
  Box,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Spacer,
  VStack,
} from "@chakra-ui/react";
import { ILottie } from "@lottielab/lottie-player";
import { createRef, RefObject, useState } from "react";

import { AuditCompanyNames, TBadgeContent } from "./BadgeContent";
import { HomeHeading } from "./HomeHeading";
import {
  HomeJumpLinks,
  ID_EARN_INCENTIVES,
  ID_PLUME_ASSETS,
  ID_TRENDING_RWAS,
  ID_YIELD_VAULTS,
} from "./HomeJumpLinks";
import { CtaCard, VaultCard } from "./VaultCard";

import { NavButton } from "@/components/nav-button";
import { PageLayout } from "@/components/page-layout";
import {
  AppRoute,
  MATRIX_DOCK_GOLD_URL,
  NEST_AUDIT_URL,
  PETH_URL,
  PUSD_URL,
  ROYCO_DOMAIN_URL,
  STAKING_URL,
  SWAP_URL,
} from "@/constants/shared";

const NEST_BADGE_CONTENT = {
  audited: {
    auditInfo: {
      names: ["slowmist", "0xmacro", "spearbit"] as AuditCompanyNames[],
      href: NEST_AUDIT_URL,
    },
  },
  liquid: {
    assets: ["pUSD", "USDC", "USDT"],
  },
  composable: {
    apps: [
      "Rooster",
      "Solera",
      "Mystic",
      "Optimizer",
      "Royco",
      "Izumi",
      "Multichainz",
      "Morpho",
      "Curve",
    ],
  },
  incentivized: {
    incentives: ["pp", "plume"] as ("pp" | "plume")[],
  },
};

const MORPHO_BADGE_CONTENT = {
  audited: {
    auditInfo: {
      names: ["spearbit", "openzeppelin", "cantina"] as AuditCompanyNames[],
      href: "https://docs.morpho.org/overview/resources/audits/",
    },
  },
  liquid: {
    assets: ["pUSD"],
  },
  composable: {
    apps: [
      "Rooster",
      "Solera",
      "Mystic",
      "Optimizer",
      "Royco",
      "Izumi",
      "Multichainz",
      "Morpho",
      "Curve",
    ],
  },
  incentivized: {
    incentives: ["pp", "plume"] as ("pp" | "plume")[],
  },
};

const XAUM_BADGE_CONTENT = {
  audited: {
    auditInfo: {
      names: ["zellic", "blocksec"] as AuditCompanyNames[],
      href: "https://matrixdock.gitbook.io/matrixdock-docs/english/gold-token-xaum/smart-contract/contract-audit",
    },
  },
  liquid: {
    assets: ["pUSD", "USDC", "USDT"],
  },
  incentivized: {
    incentives: ["pp"] as "pp"[],
  },
};

const NALPHA_BADGE_CONTENT: TBadgeContent = {
  ...NEST_BADGE_CONTENT,
  audited: {
    ...NEST_BADGE_CONTENT.audited,
    diligenceInfo: {
      names: ["cicada"],
      href: "https://app.nest.credit/nest-alpha-vault?utm_source=plume-portal&utm_content=alpha-vault-diligence",
    },
  },
};

const SIMPLE_GRID_STYLES = {
  columns: {
    base: 1,
    xl: 2,
  },
  gap: 5,
  h: [null, null, null, null, null, 639],
  w: "full",
};
const SIMPLE_GRID_STYLES_SHORT = {
  ...SIMPLE_GRID_STYLES,
  h: [null, null, null, null, null, 555],
};

const LOTTIE_REF_IDS = [
  "xaum",
  "morpho",
  "alpha",
  "payfi",
  "nest-cta",
  "nest-cta-content",
  "pusd",
  "royco-cta",
  "royco-cta-carousel",
  "plume",
  "peth",
  "pusd-native-stablecoin",
  "native-assets",
] as const;

export const HomeBody = () => {
  const [lottieRefs] = useState(
    () =>
      new Map<string, RefObject<ILottie | null>>(
        LOTTIE_REF_IDS.map((id) => [id, createRef<ILottie>()]),
      ),
  );
  const getLottieRef = (id: (typeof LOTTIE_REF_IDS)[number]) =>
    lottieRefs.get(id)!;

  return (
    <PageLayout>
      <Box>
        <Heading
          mb={2}
          fontSize={["24px", "24px", "40px"]}
          fontWeight="700"
          lineHeight={["32px", "32px", "48px"]}
          letterSpacing="-0.8px"
        >
          Start your RWAfi journey with Plume
        </Heading>
        <Heading size={["lg", "lg", "xl"]} fontWeight="500" color="fg.muted">
          Swap, lend, borrow, loop, and earn with real yields from
          institutional-grade assets
        </Heading>
        <Spacer h={4} />
        <HomeJumpLinks />
      </Box>
      <VStack gap={2} alignItems="flex-start">
        <HomeHeading id={ID_TRENDING_RWAS} mt={2}>
          Trending RWAs on Plume
        </HomeHeading>
        <Heading fontSize="lg" fontWeight="500" color="text.secondary" mt={-2}>
          Hot RWAfi opportunities from the Plume ecosystem
        </Heading>
      </VStack>
      <Box w="full" h={["auto", "auto", 555]}>
        <VaultCard
          name="XAUm"
          title="Matrixdock Gold"
          description="Backed by 99.99% purity LBMA gold, each token represents 1 troy oz. Stored in reputable vaults in Asia with physical redemption options."
          href={MATRIX_DOCK_GOLD_URL}
          icon="/assets/home/icons/matrixdock.svg"
          lottieRef={getLottieRef("xaum")}
          lottie="https://cdn.lottielab.com/l/9vRRnLoASuFUox.json"
          slotRight={
            <Box w="full" h="full" px="33px" pt="68.67px" bgColor="#372E0F">
              <Image
                src="/assets/home/xaum-right.avif"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          }
          tvl="$60M+"
          badgeContent={XAUM_BADGE_CONTENT}
          altColor="#B1D8D766"
        />
      </Box>
      <SimpleGrid {...SIMPLE_GRID_STYLES}>
        <VaultCard
          name="Morpho Re7 pUSD Vault"
          title="Incentivized Morpho Lending vault"
          description="Earn Plume incentives and base yields via  Re7's managed strategy on Morpho"
          apy="5%+"
          logos={[
            "/assets/home/logos/morpho.svg",
            "/assets/home/logos/merkl.svg",
          ]}
          href="https://app.merkl.xyz/?chain=98866"
          icon="/assets/home/icons/morpho.svg"
          lottieRef={getLottieRef("morpho")}
          lottie="https://cdn.lottielab.com/l/6WH4Bmp69UFbE9.json"
          badgeContent={MORPHO_BADGE_CONTENT}
          altColor="#B1D8D766"
        />
        {/* <VaultCard
          name="Nest Treasury Vault"
          title="Onchain U.S. treasuries"
          description="Stable, conservative yield strategy backed by short-duration U.S. Treasuries"
          apy="5%"
          logos={[
            '/assets/home/logos/superstate.svg',
            '/assets/home/logos/anemoy.svg',
            '/assets/home/logos/centrifuge.svg',
          ]}
          href="https://app.nest.credit/nest-treasury-vault"
          icon="/assets/home/icons/nest-treasury.svg"
          lottieRef={registerLottieRef('niv')}
          lottie="https://cdn.lottielab.com/l/3Wn8UaTDBgBPTq.json"
          badgeContent={NEST_BADGE_CONTENT}
          altColor="#B1D8D766"
        /> */}
        <VaultCard
          name="Nest Alpha Vault"
          title="Flagship yield strategy"
          description="Safe, liquid yields with diversified, institutional-grade assets from AAA firms, backed by robust liquidity"
          apy="10.5%"
          href="https://app.nest.credit/nest-alpha-vault?utm_source=plume-portal&utm_content=alpha-vault-card"
          icon="/assets/home/icons/nalpha.svg"
          lottieRef={getLottieRef("alpha")}
          lottie="https://cdn.lottielab.com/l/8FRYbAAWrgAYPM.json"
          badgeContent={NALPHA_BADGE_CONTENT}
          altColor="#B1D8D766"
          logos={[
            "/assets/home/logos/invesco.svg",
            "/assets/home/logos/blackstone.svg",
            "/assets/home/logos/blackrock.svg",
          ]}
        />
        {/* <VaultCard
          name="Nest ETF Vault"
          title="Highly liquid institutional ETF yields"
          description="Diversified yields via tokenized ETFs from top-tier managers"
          apy="8.8%"
          logos={[
            '/assets/home/logos/blackstone.svg',
            '/assets/home/logos/blackrock.svg',
            '/assets/home/logos/simplify.svg',
          ]}
          href="https://app.nest.credit/nest-etf-vault"
          icon="/assets/home/icons/nest-etf.svg"
          lottieRef={registerLottieRef('nest-etf')}
          lottie="https://cdn.lottielab.com/l/6siccvs2ErgsZA.json"
          badgeContent={NEST_BADGE_CONTENT}
          altColor="#B1D8D766"
        /> */}
      </SimpleGrid>
      <HomeHeading id={ID_YIELD_VAULTS} mb={-2}>
        Stake into RWA yield vaults on Nest
      </HomeHeading>
      <SimpleGrid {...SIMPLE_GRID_STYLES}>
        <VaultCard
          name="Nest Basis Vault"
          title="Basis Trade Vault"
          description="Basis trades combined with tokenized treasuries for consistent, non-directional yield."
          apy="8%"
          logos={["/assets/home/logos/superstate.svg"]}
          href="https://app.nest.credit/nest-basis-vault?utm_source=plume-portal&utm_content=basis-vault-card"
          icon="/assets/home/icons/nbasis.svg"
          lottieRef={getLottieRef("payfi")}
          lottie="https://cdn.lottielab.com/l/CX3XeMWgEq6vWj.json"
          badgeContent={NEST_BADGE_CONTENT}
          altColor="#B1D8D766"
        />
        <CtaCard
          title="Explore Nest"
          subtitle="Safe, unrestricted yields from TradFi"
          description="Start growing your wealth today with yield strategies designed for everyone"
          icon="/assets/home/icons/nest.svg"
          href="https://app.nest.credit/?utm_source=plume-portal&utm_content=explore-nest-cta"
          lottieRef={getLottieRef("nest-cta")}
          lottie="https://cdn.lottielab.com/l/9qSdtMZ2Yk9TBx.json"
          lottieContent="https://cdn.lottielab.com/l/D7urYFkNj5j8Vo.json"
          lottieContentRef={getLottieRef("nest-cta-content")}
          altColor="#B1D8D766"
        />
      </SimpleGrid>
      <HomeHeading id={ID_EARN_INCENTIVES} mb={-2}>
        Seed liquidity and earn PLUME incentives on Royco
      </HomeHeading>
      <SimpleGrid {...SIMPLE_GRID_STYLES}>
        <VaultCard
          name="Lend pUSD on Mystic Finance"
          title="Earn base yields and PLUME incentives"
          description="Lend out stables to seed liquidity on Mystic finance to earn boosted yields"
          apy="15%"
          logos={["/assets/home/logos/mystic.svg"]}
          href="https://app.mysticfinance.xyz/"
          icon="/assets/home/icons/pusd.svg"
          lottieRef={getLottieRef("pusd")}
          lottie="https://cdn.lottielab.com/l/9XYaPwMYLoX8GT.json"
          badgeContent={{
            audited: {
              auditInfo: {
                names: ["hacken"],
                href: "https://hacken.io/audits/mystic-finance",
              },
            },
            incentivized: { incentives: ["plume"] },
          }}
          altColor="#E4E6DC80"
        />
        <CtaCard
          title="Explore Royco"
          subtitle="The Incentivized action market protocol"
          description="Deposit into Royco markets and earn base yields with PLUME incentives"
          icon="/assets/home/icons/royco.svg"
          lottie="https://cdn.lottielab.com/l/DMXqsnagpV5ai2.json"
          lottieRef={getLottieRef("royco-cta")}
          href={ROYCO_DOMAIN_URL}
          lottieContent="https://cdn.lottielab.com/l/GNznixPcY8RmM0.json"
          lottieContentRef={getLottieRef("royco-cta-carousel")}
          altColor="#E4E6DC80"
        />
      </SimpleGrid>
      <HomeHeading id={ID_PLUME_ASSETS}>
        Earn on your PLUME and explore Plume native assets
      </HomeHeading>
      <SimpleGrid {...SIMPLE_GRID_STYLES_SHORT}>
        <VaultCard
          name="Plume Staking"
          title="Earn yield on your PLUME"
          description="Stake PLUME to earn base yields, access future rewards and partake in governance"
          apy="5%"
          href={STAKING_URL}
          icon="/assets/home/icons/plume.svg"
          lottieRef={getLottieRef("plume")}
          lottie="https://cdn.lottielab.com/l/3G2Tr8RdZUGH7j.json"
          badgeContent={{
            liquid: {
              assets: ["PLUME"],
            },
            incentivized: {
              incentives: ["pp", "plume"],
            },
          }}
          altColor="#EEE8E0"
        />{" "}
        <VaultCard
          name="Plume ETH"
          title="Plume’s native ETH LST"
          description="Earn passive yield on your Ethereum just by minting
and holding Plume ETH"
          apy="4.3%"
          href={PETH_URL}
          icon="/assets/home/icons/peth.svg"
          lottieRef={getLottieRef("peth")}
          lottie="https://cdn.lottielab.com/l/tGrriFj81CS1G0.json"
          badgeContent={{
            audited: {
              auditInfo: {
                names: ["shieldify", "cantina", "nethermind"],
                href: "https://github.com/dinero-protocol/audits/tree/master/dinero-pirex-eth/branded-lst",
              },
            },
            liquid: {
              assets: ["ETH", "wETH"],
            },
            composable: NEST_BADGE_CONTENT.composable,
            incentivized: { incentives: ["pp", "plume"] },
          }}
          altColor="#EEE8E0"
        />
      </SimpleGrid>
      <SimpleGrid {...SIMPLE_GRID_STYLES_SHORT}>
        <VaultCard
          name="Plume USD"
          title="Plume’s native stablecoin"
          description="Backed by institutional grade stablecoin collateral, pUSD is Plume’s canonical stable"
          href={PUSD_URL}
          icon="/assets/home/icons/pusd.svg"
          lottieRef={getLottieRef("pusd-native-stablecoin")}
          lottie="https://cdn.lottielab.com/l/5LtsHizvyMuLLu.json"
          badgeContent={{
            audited: {
              auditInfo: {
                names: ["0xmacro", "pashov", "spearbit"],
                href: "https://github.com/dinero-protocol/audits/tree/master/dinero-pirex-eth/branded-lst",
              },
            },
            liquid: {
              assets: ["USDT", "USDC"],
            },
            composable: NEST_BADGE_CONTENT.composable,
            incentivized: { incentives: ["plume"] },
          }}
          altColor="#EEE8E0"
        />
        <CtaCard
          icon="/assets/home/icons/native-assets.svg"
          title="Get Plume Native Assets"
          description="Bridge to Plume or swap assets"
          lottieRef={getLottieRef("native-assets")}
          lottie="https://cdn.lottielab.com/l/3G2Tr8RdZUGH7j.json"
          altColor="#EEE8E0"
        >
          <HStack>
            <NavButton h={11} variant="solid" href={AppRoute.Bridge}>
              Bridge
            </NavButton>
            <NavButton h={11} variant="solid" href={SWAP_URL}>
              Swap
            </NavButton>
          </HStack>
        </CtaCard>
      </SimpleGrid>
    </PageLayout>
  );
};
